
import { toString, Union, Record } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { list_type, union_type, string_type, int32_type, char_type, record_type, bool_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { append as append_1, isEmpty, length, filter, tryHead, tryFind as tryFind_1, map, collect, singleton as singleton_1, ofArray, empty } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { empty as empty_1, singleton, append, delay, toList } from "../fable_modules/fable-library-js.5.13.0/Seq.js";
import { tryFind, ofList } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { arrayHash, equalArrays, safeHash, equals, compareArrays } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { join } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { defaultArg } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { List_groupBy, List_countBy } from "../fable_modules/fable-library-js.5.13.0/Seq2.js";

/**
 * Modifier state, normalised so one table serves both platforms.
 * 
 * Primary is the "command" modifier: Ctrl on Windows and Linux, Cmd on macOS. That is the one
 * almost every shortcut wants, and normalising it here is what removes the isMac conditionals
 * that used to be written out by hand at every binding site.
 * 
 * Secondary is whichever of the two is left over. It exists for exactly one shortcut - macOS
 * full screen is Cmd+Ctrl+F - and should stay that rare.
 */
export class Mods extends Record {
    constructor(Primary, Secondary, Alt, Shift) {
        super();
        this.Primary = Primary;
        this.Secondary = Secondary;
        this.Alt = Alt;
        this.Shift = Shift;
    }
}

export function Mods_$reflection() {
    return record_type("KeyTypes.Mods", [], Mods, () => [["Primary", bool_type], ["Secondary", bool_type], ["Alt", bool_type], ["Shift", bool_type]]);
}

export const ModsModule_none = new Mods(false, false, false, false);

export const ModsModule_prim = new Mods(true, ModsModule_none.Secondary, ModsModule_none.Alt, ModsModule_none.Shift);

export const ModsModule_primShift = new Mods(ModsModule_prim.Primary, ModsModule_prim.Secondary, ModsModule_prim.Alt, true);

export const ModsModule_primAlt = new Mods(ModsModule_prim.Primary, ModsModule_prim.Secondary, true, ModsModule_prim.Shift);

export const ModsModule_primSecondary = new Mods(ModsModule_prim.Primary, true, ModsModule_prim.Alt, ModsModule_prim.Shift);

export const ModsModule_alt = new Mods(ModsModule_none.Primary, ModsModule_none.Secondary, true, ModsModule_none.Shift);

export const ModsModule_shift = new Mods(ModsModule_none.Primary, ModsModule_none.Secondary, ModsModule_none.Alt, true);

/**
 * A key identity, normalised away from the browser's raw strings.
 * 
 * Raw `ev.key` cannot be used directly: on macOS Option mangles it, so Cmd+Option+A arrives as
 * "å". Raw `ev.code` cannot be used either, since it is physical and so wrong on non-QWERTY
 * layouts. KeyBindings.keyNameOf resolves an event into one of these using `ev.key` where it is
 * meaningful and falling back to `ev.code` where it is not.
 */
export class KeyName extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["KLetter", "KDigit", "KFn", "KNamed"];
    }
}

export function KeyName_$reflection() {
    return union_type("KeyTypes.KeyName", [], KeyName, () => [[["Item", char_type]], [["Item", char_type]], [["Item", int32_type]], [["Item", string_type]]]);
}

export const Names_arrowLeft = "ArrowLeft";

export const Names_arrowRight = "ArrowRight";

export const Names_arrowUp = "ArrowUp";

export const Names_arrowDown = "ArrowDown";

export const Names_escape = "Escape";

export const Names_enter = "Enter";

export const Names_tab = "Tab";

export const Names_space = "Space";

export const Names_delete = "Delete";

export const Names_backspace = "Backspace";

export const Names_minus = "Minus";

export const Names_equal = "Equal";

export class Chord extends Record {
    constructor(Mods, Key) {
        super();
        this.Mods = Mods;
        this.Key = Key;
    }
}

export function Chord_$reflection() {
    return record_type("KeyTypes.Chord", [], Chord, () => [["Mods", Mods_$reflection()], ["Key", KeyName_$reflection()]]);
}

/**
 * Where the keyboard is logically pointing. Exactly one holds at any moment, and
 * KeyBindings.contextOfModel decides which by a fixed priority.
 * 
 * TextEntry and CodeEditor are *opaque*: a chord not bound in them does nothing rather than
 * falling back to a more general context. That single rule is what stops Ctrl+A in a properties
 * box selecting the whole schematic.
 */
export class KeyContext extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["TextEntry", "CodeEditor", "ProjectBrowser", "Popup", "WaveSim", "SheetBusy", "SheetIdle", "Global"];
    }
    static TextEntry = new KeyContext(0, []);
    static CodeEditor = new KeyContext(1, []);
    static ProjectBrowser = new KeyContext(2, []);
    static Popup = new KeyContext(3, []);
    static WaveSim = new KeyContext(4, []);
    static SheetBusy = new KeyContext(5, []);
    static SheetIdle = new KeyContext(6, []);
    static Global = new KeyContext(7, []);
}

export function KeyContext_$reflection() {
    return union_type("KeyTypes.KeyContext", [], KeyContext, () => [[], [], [], [], [], [], [], []]);
}

/**
 * Every shortcut identity in Issie.
 * 
 * Adding a case here is a compile error until KeyBindings.actionOf binds it, which is the point:
 * there is no way to describe a shortcut and forget to implement it. Several identities share a
 * chord and are told apart only by context - Escape is four different actions.
 */
export class ShortcutId extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["ScCopy", "ScPaste", "ScSelectAll", "ScDelete", "ScRotateClockwise", "ScRotateAnticlockwise", "ScFlipVertical", "ScFlipHorizontal", "ScAlign", "ScDistribute", "ScRotateLabel", "ScRenameComponent", "ScUndo", "ScRedo", "ScRedrawFloatingWires", "ScRedrawAllWires", "ScSeparateManualRouting", "ScMovePortsHelp", "ScCancelGesture", "ScClosePopup", "ScBrowserPrev", "ScBrowserNext", "ScBrowserOpen", "ScBrowserParent", "ScDeselect", "ScLeaveTextBox", "ScZoomIn", "ScZoomOut", "ScZoomToFit", "ScAppZoomIn", "ScAppZoomOut", "ScAppZoomReset", "ScFullScreen", "ScToggleGrid", "ScToggleWireArrows", "ScWireTypeJump", "ScWireTypeRadiussed", "ScWireTypeModern", "ScThemeDefault", "ScThemeLight", "ScThemeGrayscale", "ScToggleBuildTab", "ScNewSheet", "ScSaveSheet", "ScAbout", "ScQuit", "ScWaveStepBack", "ScWaveStepForward", "ScTextCopy", "ScTextCut", "ScTextPaste", "ScTextSelectAll", "ScTextUndo", "ScTextRedo", "ScSuppressScroll", "ScSwallowCloseWindow", "ScDevTools", "GsCtrlWheelZoom", "GsShiftDragPan", "GsSpaceDragPan", "GsTabBetweenBoxes"];
    }
    static ScCopy = new ShortcutId(0, []);
    static ScPaste = new ShortcutId(1, []);
    static ScSelectAll = new ShortcutId(2, []);
    static ScDelete = new ShortcutId(3, []);
    static ScRotateClockwise = new ShortcutId(4, []);
    static ScRotateAnticlockwise = new ShortcutId(5, []);
    static ScFlipVertical = new ShortcutId(6, []);
    static ScFlipHorizontal = new ShortcutId(7, []);
    static ScAlign = new ShortcutId(8, []);
    static ScDistribute = new ShortcutId(9, []);
    static ScRotateLabel = new ShortcutId(10, []);
    static ScRenameComponent = new ShortcutId(11, []);
    static ScUndo = new ShortcutId(12, []);
    static ScRedo = new ShortcutId(13, []);
    static ScRedrawFloatingWires = new ShortcutId(14, []);
    static ScRedrawAllWires = new ShortcutId(15, []);
    static ScSeparateManualRouting = new ShortcutId(16, []);
    static ScMovePortsHelp = new ShortcutId(17, []);
    static ScCancelGesture = new ShortcutId(18, []);
    static ScClosePopup = new ShortcutId(19, []);
    static ScBrowserPrev = new ShortcutId(20, []);
    static ScBrowserNext = new ShortcutId(21, []);
    static ScBrowserOpen = new ShortcutId(22, []);
    static ScBrowserParent = new ShortcutId(23, []);
    static ScDeselect = new ShortcutId(24, []);
    static ScLeaveTextBox = new ShortcutId(25, []);
    static ScZoomIn = new ShortcutId(26, []);
    static ScZoomOut = new ShortcutId(27, []);
    static ScZoomToFit = new ShortcutId(28, []);
    static ScAppZoomIn = new ShortcutId(29, []);
    static ScAppZoomOut = new ShortcutId(30, []);
    static ScAppZoomReset = new ShortcutId(31, []);
    static ScFullScreen = new ShortcutId(32, []);
    static ScToggleGrid = new ShortcutId(33, []);
    static ScToggleWireArrows = new ShortcutId(34, []);
    static ScWireTypeJump = new ShortcutId(35, []);
    static ScWireTypeRadiussed = new ShortcutId(36, []);
    static ScWireTypeModern = new ShortcutId(37, []);
    static ScThemeDefault = new ShortcutId(38, []);
    static ScThemeLight = new ShortcutId(39, []);
    static ScThemeGrayscale = new ShortcutId(40, []);
    static ScToggleBuildTab = new ShortcutId(41, []);
    static ScNewSheet = new ShortcutId(42, []);
    static ScSaveSheet = new ShortcutId(43, []);
    static ScAbout = new ShortcutId(44, []);
    static ScQuit = new ShortcutId(45, []);
    static ScWaveStepBack = new ShortcutId(46, []);
    static ScWaveStepForward = new ShortcutId(47, []);
    static ScTextCopy = new ShortcutId(48, []);
    static ScTextCut = new ShortcutId(49, []);
    static ScTextPaste = new ShortcutId(50, []);
    static ScTextSelectAll = new ShortcutId(51, []);
    static ScTextUndo = new ShortcutId(52, []);
    static ScTextRedo = new ShortcutId(53, []);
    static ScSuppressScroll = new ShortcutId(54, []);
    static ScSwallowCloseWindow = new ShortcutId(55, []);
    static ScDevTools = new ShortcutId(56, []);
    static GsCtrlWheelZoom = new ShortcutId(57, []);
    static GsShiftDragPan = new ShortcutId(58, []);
    static GsSpaceDragPan = new ShortcutId(59, []);
    static GsTabBetweenBoxes = new ShortcutId(60, []);
}

export function ShortcutId_$reflection() {
    return union_type("KeyTypes.ShortcutId", [], ShortcutId, () => [[], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], []]);
}

export class Trigger extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Chords", "Gesture", "HelpKeys"];
    }
}

export function Trigger_$reflection() {
    return union_type("KeyTypes.Trigger", [], Trigger, () => [[["win", list_type(Chord_$reflection())], ["mac", list_type(Chord_$reflection())]], [["win", string_type], ["mac", string_type]], [["win", list_type(Chord_$reflection())], ["mac", list_type(Chord_$reflection())]]]);
}

export class Category extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["CatFile", "CatEdit", "CatView", "CatWaveSim", "CatTextEntry", "CatGesture", "CatDev"];
    }
    static CatFile = new Category(0, []);
    static CatEdit = new Category(1, []);
    static CatView = new Category(2, []);
    static CatWaveSim = new Category(3, []);
    static CatTextEntry = new Category(4, []);
    static CatGesture = new Category(5, []);
    static CatDev = new Category(6, []);
}

export function Category_$reflection() {
    return union_type("KeyTypes.Category", [], Category, () => [[], [], [], [], [], [], []]);
}

export class ShortcutSpec extends Record {
    constructor(Id, Trigger, Contexts, PreventDefault, AllowRepeat, Doc, Category, DevOnly) {
        super();
        this.Id = Id;
        this.Trigger = Trigger;
        this.Contexts = Contexts;
        this.PreventDefault = PreventDefault;
        this.AllowRepeat = AllowRepeat;
        this.Doc = Doc;
        this.Category = Category;
        this.DevOnly = DevOnly;
    }
}

export function ShortcutSpec_$reflection() {
    return record_type("KeyTypes.ShortcutSpec", [], ShortcutSpec, () => [["Id", ShortcutId_$reflection()], ["Trigger", Trigger_$reflection()], ["Contexts", list_type(KeyContext_$reflection())], ["PreventDefault", bool_type], ["AllowRepeat", bool_type], ["Doc", string_type], ["Category", Category_$reflection()], ["DevOnly", bool_type]]);
}

function spec(id, trigger, contexts, doc, category) {
    return new ShortcutSpec(id, trigger, contexts, true, false, doc, category, false);
}

function repeating(s) {
    return new ShortcutSpec(s.Id, s.Trigger, s.Contexts, s.PreventDefault, true, s.Doc, s.Category, s.DevOnly);
}

function devOnly(s) {
    return new ShortcutSpec(s.Id, s.Trigger, s.Contexts, s.PreventDefault, s.AllowRepeat, s.Doc, s.Category, true);
}

function ch(mods, key) {
    return new Chord(mods, key);
}

function letter(c) {
    return new KeyName(/* KLetter */ 0, [c]);
}

function named(n) {
    return new KeyName(/* KNamed */ 3, [n]);
}

function both(chords) {
    return new Trigger(/* Chords */ 0, [chords, chords]);
}

function winOnly(chords) {
    return new Trigger(/* Chords */ 0, [chords, empty()]);
}

function macOnly(chords) {
    return new Trigger(/* Chords */ 0, [empty(), chords]);
}

function bothHelpOnly(chords) {
    return new Trigger(/* HelpKeys */ 2, [chords, chords]);
}

const sheet = ofArray([KeyContext.SheetIdle, KeyContext.SheetBusy]);

const appWide = ofArray([KeyContext.SheetIdle, KeyContext.SheetBusy, KeyContext.WaveSim, KeyContext.Global]);

const everywhere = ofArray([KeyContext.SheetIdle, KeyContext.SheetBusy, KeyContext.WaveSim, KeyContext.Global, KeyContext.Popup, KeyContext.ProjectBrowser, KeyContext.TextEntry, KeyContext.CodeEditor]);

export const shortcuts = toList(delay(() => append(singleton(spec(ShortcutId.ScCopy, both(singleton_1(ch(ModsModule_prim, letter("C")))), singleton_1(KeyContext.SheetIdle), "Copy selected items", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScPaste, both(singleton_1(ch(ModsModule_prim, letter("V")))), singleton_1(KeyContext.SheetIdle), "Paste items", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScSelectAll, both(singleton_1(ch(ModsModule_prim, letter("A")))), singleton_1(KeyContext.SheetIdle), "Select all items", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScDelete, new Trigger(/* Chords */ 0, [singleton_1(ch(ModsModule_none, named(Names_delete))), ofArray([ch(ModsModule_none, named(Names_backspace)), ch(ModsModule_none, named(Names_delete))])]), singleton_1(KeyContext.SheetIdle), "Delete items", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScRotateClockwise, both(singleton_1(ch(ModsModule_prim, named(Names_arrowRight)))), singleton_1(KeyContext.SheetIdle), "Rotate items clockwise", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScRotateAnticlockwise, both(singleton_1(ch(ModsModule_prim, named(Names_arrowLeft)))), singleton_1(KeyContext.SheetIdle), "Rotate items anticlockwise", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScFlipVertical, both(singleton_1(ch(ModsModule_prim, named(Names_arrowUp)))), singleton_1(KeyContext.SheetIdle), "Flip items vertically", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScFlipHorizontal, both(singleton_1(ch(ModsModule_prim, named(Names_arrowDown)))), singleton_1(KeyContext.SheetIdle), "Flip items horizontally", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScAlign, both(singleton_1(ch(ModsModule_primShift, letter("A")))), singleton_1(KeyContext.SheetIdle), "Align items", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScDistribute, both(singleton_1(ch(ModsModule_primShift, letter("D")))), singleton_1(KeyContext.SheetIdle), "Distribute items", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScRotateLabel, both(singleton_1(ch(ModsModule_primShift, letter("R")))), singleton_1(KeyContext.SheetIdle), "Rotate label of item", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScRenameComponent, both(singleton_1(ch(ModsModule_none, new KeyName(/* KFn */ 2, [2])))), singleton_1(KeyContext.SheetIdle), "Rename the selected component", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScUndo, both(singleton_1(ch(ModsModule_prim, letter("Z")))), singleton_1(KeyContext.SheetIdle), "Undo diagram action", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScRedo, new Trigger(/* Chords */ 0, [singleton_1(ch(ModsModule_prim, letter("Y"))), singleton_1(ch(ModsModule_primShift, letter("Z")))]), singleton_1(KeyContext.SheetIdle), "Redo diagram action", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScRedrawFloatingWires, both(empty()), singleton_1(KeyContext.SheetIdle), "Redraw every wire not routed by hand", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScRedrawAllWires, both(empty()), singleton_1(KeyContext.SheetIdle), "Redraw every wire, hand routing included", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScSeparateManualRouting, both(empty()), singleton_1(KeyContext.SheetIdle), "Line hand-routed wires up with the rest, without re-routing them", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScMovePortsHelp, both(empty()), singleton_1(KeyContext.SheetIdle), "", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScCancelGesture, both(singleton_1(ch(ModsModule_none, named(Names_escape)))), singleton_1(KeyContext.SheetBusy), "Cancel the action in progress", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScClosePopup, both(singleton_1(ch(ModsModule_none, named(Names_escape)))), ofArray([KeyContext.Popup, KeyContext.ProjectBrowser]), "Close the open dialogue", Category.CatEdit)), delay(() => append(singleton(repeating(spec(ShortcutId.ScBrowserPrev, both(singleton_1(ch(ModsModule_none, named(Names_arrowUp)))), singleton_1(KeyContext.ProjectBrowser), "", Category.CatView))), delay(() => append(singleton(repeating(spec(ShortcutId.ScBrowserNext, both(singleton_1(ch(ModsModule_none, named(Names_arrowDown)))), singleton_1(KeyContext.ProjectBrowser), "", Category.CatView))), delay(() => append(singleton(spec(ShortcutId.ScBrowserOpen, both(singleton_1(ch(ModsModule_none, named(Names_enter)))), singleton_1(KeyContext.ProjectBrowser), "", Category.CatView)), delay(() => append(singleton(spec(ShortcutId.ScBrowserParent, both(singleton_1(ch(ModsModule_none, named(Names_backspace)))), singleton_1(KeyContext.ProjectBrowser), "", Category.CatView)), delay(() => append(singleton(spec(ShortcutId.ScDeselect, both(singleton_1(ch(ModsModule_none, named(Names_escape)))), singleton_1(KeyContext.SheetIdle), "Deselect everything", Category.CatEdit)), delay(() => append(singleton(spec(ShortcutId.ScLeaveTextBox, both(ofArray([ch(ModsModule_none, named(Names_enter)), ch(ModsModule_none, named(Names_escape))])), singleton_1(KeyContext.TextEntry), "Leave an input box, returning the keyboard to the schematic", Category.CatTextEntry)), delay(() => {
    const zoomIn = ofArray([ch(ModsModule_prim, named(Names_equal)), ch(ModsModule_primShift, named(Names_equal))]);
    const zoomOut = singleton_1(ch(ModsModule_prim, named(Names_minus)));
    const zoomReset = singleton_1(ch(ModsModule_prim, new KeyName(/* KDigit */ 1, ["0"])));
    const zoomable = ofArray([KeyContext.SheetIdle, KeyContext.SheetBusy, KeyContext.WaveSim, KeyContext.TextEntry]);
    return append(singleton(repeating(spec(ShortcutId.ScZoomIn, both(zoomIn), zoomable, "Zoom in: the schematic, or the waveforms if the wave simulator has the keyboard", Category.CatView))), delay(() => append(singleton(repeating(spec(ShortcutId.ScZoomOut, both(zoomOut), zoomable, "Zoom out: the schematic, or the waveforms if the wave simulator has the keyboard", Category.CatView))), delay(() => append(singleton(spec(ShortcutId.ScZoomToFit, both(zoomReset), zoomable, "Fit the whole circuit on the screen", Category.CatView)), delay(() => {
        let bind$0040;
        return append(singleton(repeating(spec(ShortcutId.ScAppZoomIn, both(ofArray([ch(ModsModule_primAlt, named(Names_equal)), ch((bind$0040 = ModsModule_primAlt, new Mods(bind$0040.Primary, bind$0040.Secondary, bind$0040.Alt, true)), named(Names_equal))])), everywhere, "Zoom whole application in", Category.CatView))), delay(() => append(singleton(repeating(spec(ShortcutId.ScAppZoomOut, both(singleton_1(ch(ModsModule_primAlt, named(Names_minus)))), everywhere, "Zoom whole application out", Category.CatView))), delay(() => append(singleton(spec(ShortcutId.ScAppZoomReset, both(singleton_1(ch(ModsModule_primAlt, new KeyName(/* KDigit */ 1, ["0"])))), everywhere, "Zoom whole application reset", Category.CatView)), delay(() => append(singleton(spec(ShortcutId.ScFullScreen, new Trigger(/* Chords */ 0, [singleton_1(ch(ModsModule_none, new KeyName(/* KFn */ 2, [11]))), singleton_1(ch(ModsModule_primSecondary, letter("F")))]), appWide, "Enter/exit fullscreen", Category.CatView)), delay(() => append(singleton(spec(ShortcutId.ScToggleGrid, both(singleton_1(ch(ModsModule_primAlt, letter("G")))), sheet, "Show/hide grid lines", Category.CatView)), delay(() => append(singleton(spec(ShortcutId.ScToggleWireArrows, both(singleton_1(ch(ModsModule_primAlt, letter("W")))), sheet, "Show/hide wire arrows", Category.CatView)), delay(() => append(singleton(spec(ShortcutId.ScWireTypeJump, both(empty()), sheet, "", Category.CatView)), delay(() => append(singleton(spec(ShortcutId.ScWireTypeRadiussed, both(empty()), sheet, "", Category.CatView)), delay(() => append(singleton(spec(ShortcutId.ScWireTypeModern, both(empty()), sheet, "", Category.CatView)), delay(() => append(singleton(spec(ShortcutId.ScThemeDefault, both(empty()), sheet, "", Category.CatView)), delay(() => append(singleton(spec(ShortcutId.ScThemeLight, both(empty()), sheet, "", Category.CatView)), delay(() => append(singleton(spec(ShortcutId.ScThemeGrayscale, both(empty()), sheet, "", Category.CatView)), delay(() => append(singleton(spec(ShortcutId.ScToggleBuildTab, both(empty()), appWide, "", Category.CatView)), delay(() => append(singleton(spec(ShortcutId.ScNewSheet, both(singleton_1(ch(ModsModule_prim, letter("N")))), appWide, "Create new sheet", Category.CatFile)), delay(() => append(singleton(spec(ShortcutId.ScSaveSheet, both(singleton_1(ch(ModsModule_prim, letter("S")))), appWide, "Save current sheet", Category.CatFile)), delay(() => append(singleton(spec(ShortcutId.ScAbout, both(singleton_1(ch(ModsModule_none, new KeyName(/* KFn */ 2, [1])))), appWide, "Open the Info window", Category.CatFile)), delay(() => append(singleton(spec(ShortcutId.ScSwallowCloseWindow, winOnly(singleton_1(ch(ModsModule_prim, letter("W")))), appWide, "", Category.CatFile)), delay(() => append(singleton(spec(ShortcutId.ScQuit, macOnly(singleton_1(ch(ModsModule_prim, letter("Q")))), appWide, "Quit application", Category.CatFile)), delay(() => append(singleton(repeating(spec(ShortcutId.ScWaveStepBack, both(singleton_1(ch(ModsModule_none, named(Names_arrowLeft)))), singleton_1(KeyContext.WaveSim), "Move the waveform cursor back one clock cycle", Category.CatWaveSim))), delay(() => append(singleton(repeating(spec(ShortcutId.ScWaveStepForward, both(singleton_1(ch(ModsModule_none, named(Names_arrowRight)))), singleton_1(KeyContext.WaveSim), "Move the waveform cursor forward one clock cycle", Category.CatWaveSim))), delay(() => append(singleton(spec(ShortcutId.ScTextCopy, macOnly(singleton_1(ch(ModsModule_prim, letter("C")))), singleton_1(KeyContext.TextEntry), "", Category.CatTextEntry)), delay(() => append(singleton(spec(ShortcutId.ScTextCut, macOnly(singleton_1(ch(ModsModule_prim, letter("X")))), singleton_1(KeyContext.TextEntry), "", Category.CatTextEntry)), delay(() => append(singleton(spec(ShortcutId.ScTextPaste, macOnly(singleton_1(ch(ModsModule_prim, letter("V")))), singleton_1(KeyContext.TextEntry), "", Category.CatTextEntry)), delay(() => append(singleton(spec(ShortcutId.ScTextSelectAll, macOnly(singleton_1(ch(ModsModule_prim, letter("A")))), singleton_1(KeyContext.TextEntry), "", Category.CatTextEntry)), delay(() => append(singleton(spec(ShortcutId.ScTextUndo, macOnly(singleton_1(ch(ModsModule_prim, letter("Z")))), singleton_1(KeyContext.TextEntry), "", Category.CatTextEntry)), delay(() => append(singleton(spec(ShortcutId.ScTextRedo, macOnly(singleton_1(ch(ModsModule_primShift, letter("Z")))), singleton_1(KeyContext.TextEntry), "", Category.CatTextEntry)), delay(() => append(singleton(repeating(spec(ShortcutId.ScSuppressScroll, both(singleton_1(ch(ModsModule_none, named(Names_space)))), ofArray([KeyContext.SheetIdle, KeyContext.SheetBusy, KeyContext.WaveSim, KeyContext.Popup, KeyContext.Global]), "", Category.CatView))), delay(() => append(singleton(devOnly(spec(ShortcutId.ScDevTools, new Trigger(/* Chords */ 0, [singleton_1(ch(ModsModule_primShift, letter("I"))), singleton_1(ch(ModsModule_primAlt, letter("I")))]), appWide, "Show/hide browser developer tools", Category.CatDev))), delay(() => append(singleton(spec(ShortcutId.GsCtrlWheelZoom, new Trigger(/* Gesture */ 1, ["Control + mouse wheel", "Command-mouse wheel"]), empty(), "Zoom the diagram", Category.CatGesture)), delay(() => append(singleton(spec(ShortcutId.GsSpaceDragPan, new Trigger(/* Gesture */ 1, ["Space + drag on canvas", "Space-drag on canvas"]), empty(), "Pan the schematic", Category.CatGesture)), delay(() => append(singleton(spec(ShortcutId.GsShiftDragPan, new Trigger(/* Gesture */ 1, ["Shift + drag on canvas", "Shift-drag on canvas"]), empty(), "", Category.CatGesture)), delay(() => {
            let bind$0040_1;
            return singleton(spec(ShortcutId.GsTabBetweenBoxes, bothHelpOnly(ofArray([ch(ModsModule_none, named(Names_tab)), ch((bind$0040_1 = ModsModule_none, new Mods(bind$0040_1.Primary, bind$0040_1.Secondary, bind$0040_1.Alt, true)), named(Names_tab))])), empty(), "Move between input boxes in the properties pane", Category.CatGesture));
        }))))))))))))))))))))))))))))))))))))))))))))))))))))))))))))));
    }))))));
}))))))))))))))))))))))))))))))))))))))))))))))))))))));

/**
 * Chords for one platform - the ones the dispatcher fires on, so help-only keys are not among
 * them: this is what builds the lookup table.
 */
export function chordsFor(isMac, spec_1) {
    const matchValue = spec_1.Trigger;
    switch (matchValue.tag) {
        case 1:
        case 2:
            return empty();
        default:
            if (isMac) {
                return matchValue.fields[1];
            }
            else {
                return matchValue.fields[0];
            }
    }
}

/**
 * The keys to SHOW for one platform: what the dispatcher fires on, or - for a shortcut the
 * browser acts on rather than Issie - what the user presses anyway.
 */
export function shownChordsFor(isMac, spec_1) {
    const matchValue = spec_1.Trigger;
    let matchResult, mac, win;
    switch (matchValue.tag) {
        case 2: {
            matchResult = 0;
            mac = matchValue.fields[1];
            win = matchValue.fields[0];
            break;
        }
        case 1: {
            matchResult = 1;
            break;
        }
        default: {
            matchResult = 0;
            mac = matchValue.fields[1];
            win = matchValue.fields[0];
        }
    }
    switch (matchResult) {
        case 0:
            if (isMac) {
                return mac;
            }
            else {
                return win;
            }
        default:
            return empty();
    }
}

/**
 * (context, chord) -> spec, for one platform. Built once per platform by the caller.
 */
export function table(isMac) {
    return ofList(collect((s) => collect((c) => map((ctx) => [[ctx, c], s], s.Contexts), chordsFor(isMac, s)), shortcuts), {
        Compare: (x, y) => (compareArrays(x, y) | 0),
    });
}

/**
 * Resolve a chord in a context.
 * 
 * There is deliberately no fallback to a more general context. A chord that means nothing in
 * TextEntry must reach the focused box rather than doing something to the schematic, and the same
 * argument applies to every other context - so every shortcut states all the contexts it works in.
 */
export function lookup(tbl, ctx, chord) {
    return tryFind([ctx, chord], tbl);
}

function keyShortName(key) {
    switch (key.tag) {
        case 1:
            return key.fields[0];
        case 2:
            return `F${key.fields[0]}`;
        case 3:
            if (key.fields[0] === Names_arrowLeft) {
                return "Left";
            }
            else if (key.fields[0] === Names_arrowRight) {
                return "Right";
            }
            else if (key.fields[0] === Names_arrowUp) {
                return "Up";
            }
            else if (key.fields[0] === Names_arrowDown) {
                return "Down";
            }
            else if (key.fields[0] === Names_equal) {
                return "+";
            }
            else if (key.fields[0] === Names_minus) {
                return "-";
            }
            else if (key.fields[0] === Names_escape) {
                return "Esc";
            }
            else if (key.fields[0] === Names_delete) {
                return "Del";
            }
            else if (key.fields[0] === Names_enter) {
                return "Return";
            }
            else {
                return key.fields[0];
            }
        default:
            return key.fields[0];
    }
}

/**
 * The keys of a chord, short form, one per element: [ "Ctrl"; "Alt"; "-" ]. A list rather than a
 * string so that a caller showing the keys THEMSELVES can draw one key per element; joined with
 * "+" it is the compact label a menu wants.
 */
export function chordShortParts(isMac, chord) {
    const m = chord.Mods;
    return toList(delay(() => append(m.Primary ? singleton(isMac ? "Cmd" : "Ctrl") : empty_1(), delay(() => append(m.Secondary ? singleton(isMac ? "Ctrl" : "Meta") : empty_1(), delay(() => append(m.Alt ? singleton(isMac ? "Opt" : "Alt") : empty_1(), delay(() => append(m.Shift ? singleton("Shift") : empty_1(), delay(() => singleton(keyShortName(chord.Key))))))))))));
}

/**
 * Compact form for a menu label, e.g. "Ctrl+Right" or "Cmd+Opt+Right".
 */
export function chordLabel(isMac, chord) {
    return join("+", chordShortParts(isMac, chord));
}

/**
 * The chord a shortcut fires on, on this platform: the first one, since a shortcut with several
 * lists the one it is best known by first. None when it has no chord here.
 */
export function idChord(isMac, id) {
    const option_1 = tryFind_1((s) => equals(s.Id, id), shortcuts);
    if (option_1 != null) {
        return tryHead(chordsFor(isMac, option_1));
    }
    else {
        return undefined;
    }
}

/**
 * The keys to press for a shortcut, short form, one per element. Empty when it has no chord on
 * this platform - which is the only honest thing to show, and why anything telling the user which
 * keys to press asks the table rather than writing them out.
 */
export function idShortParts(isMac, id) {
    let option_1;
    return defaultArg((option_1 = idChord(isMac, id), (option_1 != null) ? chordShortParts(isMac, option_1) : undefined), empty());
}

/**
 * The label to show beside a menu item, or "" when the shortcut has no chord on this platform.
 */
export function idLabel(isMac, id) {
    let option_1;
    return defaultArg((option_1 = idChord(isMac, id), (option_1 != null) ? chordLabel(isMac, option_1) : undefined), "");
}

/**
 * Problems with the table, as human-readable strings. Empty means the table is sound.
 * Called at startup in debug builds: a shortcut that can never fire because another one shadows
 * it is invisible at runtime and very hard to notice by hand.
 */
export function validate() {
    const duplicateIds = map((tupledArg_1) => (`ShortcutId ${tupledArg_1[0]} appears ${tupledArg_1[1]} times`), filter((tupledArg) => (tupledArg[1] > 1), List_countBy((s) => s.Id, shortcuts, {
        Equals: equals,
        GetHashCode: (x) => (safeHash(x) | 0),
    })));
    const clashesOn = (isMac, name) => map((tupledArg_3) => {
        const _arg_2 = tupledArg_3[0];
        const ids = join(", ", map((arg) => toString(arg[1]), tupledArg_3[1]));
        return `${name}: ${chordLabel(isMac, _arg_2[1])} in ${_arg_2[0]} is claimed by ${ids}`;
    }, filter((tupledArg_2) => (length(tupledArg_2[1]) > 1), List_groupBy((tuple) => tuple[0], collect((s_1) => collect((c) => map((ctx) => [[ctx, c], s_1.Id], s_1.Contexts), chordsFor(isMac, s_1)), shortcuts), {
        Equals: equalArrays,
        GetHashCode: (x_1) => (arrayHash(x_1) | 0),
    })));
    const helpOnlyHaveNoContexts = map((s_3) => (`${s_3.Id} is shown in help only but declares contexts, which can never match`), filter((s_2) => {
        const matchValue = s_2.Trigger;
        switch (matchValue.tag) {
            case 0:
                return false;
            default:
                return !isEmpty(s_2.Contexts);
        }
    }, shortcuts));
    return append_1(duplicateIds, append_1(clashesOn(false, "windows"), append_1(clashesOn(true, "macos"), helpOnlyHaveNoContexts)));
}

